// Navigation d'un élément de chapitre vers le PRÉCÉDENT / le SUIVANT.
//
// Le même `switch (type)` était recopié dans chaque écran (scène, chat, fiche, quiz, tâche),
// et uniquement dans le sens « suivant » — d'où une flèche « ← » grise partout. Une seule
// implémentation ici : les écrans se contentent de brancher `goPrev`/`goNext` sur HeaderNav.
//
// La SÉQUENCE du chapitre voyage dans les params (`itemSeq` + `itemIndex`), et non les seuls
// voisins immédiats : un écran ne connaît pas le chapitre, il ne pouvait donc pas transmettre
// les voisins de l'élément vers lequel il navigue. Le context se perdait dès le premier
// « → » et l'élément d'après se croyait dernier — son « ✓ » renvoyait à la timeline au lieu
// d'enchaîner. En portant la séquence, chaque saut la repasse intacte.

export interface SeqItem {
  id: string | number;
  type: string;
  title?: string;
  sceneNumber?: number;
  /** Rang 1-based du chapitre PORTEUR (CCT-164) : la séquence traverse les chapters, chaque
   *  saut doit donc dire dans lequel il atterrit — sinon l'écran garde l'ancien et affiche la
   *  progression du mauvais chapitre. */
  chapterRank?: number;
}

/**
 * Séquence de navigation couvrant TOUTE l'histoire (CCT-164), à plat, dans l'ordre de lecture.
 *
 * ⚠️ `sceneNumber` n'est pas décoratif : l'écran de scène résout son contenu par le RANG de la
 * scène DANS SON CHAPITRE (`chapters[chapterId-1].story[sceneNumber-1]`), pas par l'id de
 * l'élément. Omis, il retombait en silence sur la scène bundlée n°1 — le « → » de fin de
 * chapitre affichait alors une scène d'une AUTRE mission. Le compteur repart donc à chaque
 * chapitre, comme côté timeline.
 *
 * ⚠️ CONTENU VERROUILLÉ EXCLU (CCT-258). Cette séquence traverse TOUS les chapters : la flèche
 * « → » du dernier élément d'un chapitre ouvrait donc le premier élément du SUIVANT, même
 * verrouillé. La timeline affichait bien le cadenas, mais la flèche passait à côté — un seul
 * des deux chemins respectait la règle.
 *
 * Le serveur fait déjà foi : il réduit un chapitre verrouillé à sa coquille et pose
 * `locked: true` sur ses items (idem après une barrière bloquante non franchie, CCT-65). Le
 * contenu n'est même pas téléchargé — la flèche menait donc à un élément VIDE. On s'aligne sur
 * ce marquage plutôt que de recalculer le déblocage côté client : deux règles de déblocage
 * finiraient par diverger.
 */
export function buildNavSequence(
  chapters: {
    locked?: boolean;
    sequence?: { id?: string | number; type?: string; title?: string; locked?: boolean }[];
  }[],
): SeqItem[] {
  return chapters.flatMap((c, ci) => {
    if (c.locked === true) return [];
    let scenes = 0;
    const out: SeqItem[] = [];
    for (const it of c.sequence ?? []) {
      const type = it.type === 'story' ? 'scene' : String(it.type ?? '');
      // Le compteur de scènes avance AVANT le filtre : il numérote la scène dans son chapitre,
      // et ce rang doit rester le même que la scène soit livrée ou non.
      if (type === 'scene') scenes++;
      if (it.locked === true) continue;
      out.push({
        id: String(it.id), type, title: it.title, chapterRank: ci + 1,
        ...(type === 'scene' ? { sceneNumber: scenes } : {}),
      });
    }
    return out;
  });
}

const SCREEN_BY_TYPE: Record<string, string> = {
  scene: 'SceneReader',
  chat: 'MissionChat',
  card: 'MissionCards',
  quiz: 'Quiz',
  task: 'TaskDetail',
};

interface Nav {
  goBack: () => void;
  navigate: (screen: string, params: any) => void;
  replace?: (screen: string, params: any) => void;
}

/** `goPrev`/`goNext` : `undefined` s'il n'y a pas de voisin (→ flèche désactivée). */
/**
 * @param idAffiche Identifiant de l'élément RÉELLEMENT à l'écran, quand il diffère de celui
 *   par lequel on est entré. Un écran peut en agréger plusieurs : les fiches d'un chapitre
 *   sont paginées dans une seule vue alors que CHACUNE est un élément de séquence.
 *
 *   ⚠️ SANS CE PARAMÈTRE, LA NAVIGATION MENT. `itemIndex` est figé à l'entrée : depuis la
 *   6ᵉ fiche, « → » repartait du voisin de la PREMIÈRE — on tournait en rond dans les fiches
 *   au lieu d'avancer dans la mission. Le rang doit se déduire de ce qui est affiché.
 */
export function useItemNav(route: any, navigation: Nav, displayedId?: string | number | null, fallbackSequence: SeqItem[] = []) {
  const p = route?.params ?? {};
  // URL parameters cannot carry a JavaScript object array. Rebuild from the
  // authenticated, server-gated scenario after a reload; never infer unlocks.
  const seq: SeqItem[] = Array.isArray(p.itemSeq) ? p.itemSeq
    : fallbackSequence;
  const indexEntree: number = typeof p.itemIndex === 'number' ? p.itemIndex : -1;
  const currentId = displayedId ?? p.itemId;
  const displayedIndex = currentId == null
    ? -1
    : seq.findIndex((s) => String(s.id) === String(currentId));
  // L'élément affiché fait foi quand on sait le situer ; sinon on garde celui d'entrée —
  // retomber sur -1 supprimerait les deux flèches.
  const index = displayedIndex >= 0 ? displayedIndex : indexEntree;

  const neighbour = (side: 'prev' | 'next') => {
    if (index < 0) return null;
    const i = side === 'prev' ? index - 1 : index + 1;
    return seq[i] ? { item: seq[i], index: i } : null;
  };

  const go = (side: 'prev' | 'next') => {
    const n = neighbour(side);
    if (!n) return;
    const screen = SCREEN_BY_TYPE[n.item.type];
    if (!screen) { navigation.goBack(); return; }
    const params = {
      missionId: p.missionId,
      chapterId: n.item.chapterRank ?? p.chapterId,
      itemId: n.item.id,
      title: n.item.title,
      sceneNumber: n.item.sceneNumber,
      itemSeq: seq,        // ← le context suit l'élément, sinon il meurt au premier saut
      // Même règle pour les titres (CCT-378) : sans eux, le header perdait la mission et le
      // chapitre dès le premier « → » et retombait sur un libellé générique.
      missionTitle: p.missionTitle, chapterTitle: p.chapterTitle,
      chapterSubtitle: p.chapterSubtitle,
      itemIndex: n.index,
    };
    // `replace` quand il existe (évite d'empiler l'historique en enchaînant les éléments) ;
    // en rendu INLINE, seul `navigate` est fourni par ChapterDetailScreen.
    (navigation.replace ?? navigation.navigate)(screen, params);
  };

  const hasNext = !!neighbour('next');
  return {
    goPrev: neighbour('prev') ? () => go('prev') : undefined,
    // Sur le DERNIER élément seulement, « ✓ » ferme l'élément et rend la main au chapitre.
    goNext: hasNext ? () => go('next') : () => navigation.goBack(),
    isLast: !hasNext,
  };
}
